import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { apiErr } from "@/lib/format";
import TemplatePicker, { withTemplate } from "@/components/TemplatePicker";
import { toast } from "sonner";
import { PrinterIcon, ArrowLeftIcon, DownloadSimpleIcon, EnvelopeIcon, FloppyDiskIcon, TruckIcon } from "@phosphor-icons/react";
import { EmailHistory, SendDocEmailModal } from "@/components/DocEmail";
import TransportForm, { EMPTY_TRANSPORT } from "@/pages/_guide/TransportForm";

/**
 * Guía de Remisión (documento de transporte). La vista previa y la impresión usan
 * SIEMPRE el PDF oficial del servidor (media carta institucional, una sola hoja).
 * La vista previa es borrador (no asigna número); Descargar / Imprimir / Enviar emiten.
 */
export default function ShippingGuide() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const destinoId = searchParams.get("destino_id");
  const [data, setData] = useState(null);
  const [tr, setTr] = useState(EMPTY_TRANSPORT);
  const [bultos, setBultos] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const frameRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const url = `/invoices/${id}/shipping-guide${destinoId ? `?destino_id=${encodeURIComponent(destinoId)}` : ""}`;
      const { data } = await api.get(url);
      setData(data);
      const t = data.transporte || {};
      setTr({ ...EMPTY_TRANSPORT, motivo_traslado: t.motivo_traslado || "Venta",
              fecha_inicio_traslado: t.fecha_inicio_traslado || "", observaciones: t.observaciones || "" });
      setBultos(data?.bultos != null ? String(data.bultos) : "");
    } catch (e) { toast.error(apiErr(e)); }
  }, [id, destinoId]);

  useEffect(() => { load(); }, [load]);

  const pdfBlob = useCallback(async (borrador) => {
    const params = new URLSearchParams();
    if (destinoId) params.set("destino_id", destinoId);
    if (bultos !== "") params.set("bultos", String(parseInt(bultos, 10) || 0));
    if (borrador) params.set("borrador", "true");
    const res = await api.get(withTemplate(`/shipping-guides/${id}/pdf?${params.toString()}`, "guia_remision"),
      { responseType: "blob" });
    return res.data;
  }, [id, destinoId, bultos]);

  const refreshPreview = useCallback(async (borrador = true) => {
    try {
      const blob = await pdfBlob(borrador);
      setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(blob); });
    } catch (e) { toast.error(`No se pudo generar la vista previa: ${apiErr(e)}`); }
  }, [pdfBlob]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (data) refreshPreview(!data.numero_guia); }, [data]);

  const save = async (silent = false) => {
    setBusy(true);
    try {
      const body = { ...tr };
      if (bultos !== "") body.bultos = parseInt(bultos, 10) || 0;
      await api.put(`/shipping-guides/${id}/transporte`, body);
      if (!silent) toast.success("Datos de transporte guardados");
      await refreshPreview(!data?.numero_guia);
      return true;
    } catch (e) { toast.error(apiErr(e)); return false; } finally { setBusy(false); }
  };

  // Emitir: guarda, asigna número (si falta) y devuelve el PDF definitivo
  const emit = async () => {
    if (!(await save(true))) return null;
    const blob = await pdfBlob(false);
    await load();
    return blob;
  };

  const download = async () => {
    try {
      const blob = await emit(); if (!blob) return;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `guia-remision-${data?.numero_guia || data?.factura_numero || id}.pdf`;
      a.click();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const print = async () => {
    try {
      const blob = await emit(); if (!blob) return;
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setTimeout(() => { try { frameRef.current?.contentWindow?.print(); } catch { window.open(url, "_blank"); } }, 700);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <Layout title="Guía de Remisión"><div className="p-8 text-center text-text-muted">Cargando guía…</div></Layout>;

  return (
    <Layout
      title="Guía de Remisión"
      subtitle={`${data.numero_guia ? `Guía ${data.numero_guia}` : "Sin emitir"} · ${data.factura_numero ? `Factura ${data.factura_numero}` : "Sin factura relacionada"} · ${data.destinatario?.cliente || ""}`}
      actions={
        <button onClick={() => navigate(-1)} data-testid="back-btn"
          className="flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-md text-text-secondary hover:text-text-primary">
          <ArrowLeftIcon size={14} /> Volver
        </button>
      }
    >
      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-4">
        <div className="bg-card border border-border/60 rounded-lg p-4 h-fit">
          <div className="flex items-center gap-2 mb-3">
            <TruckIcon size={18} className="text-agro-green" />
            <div className="font-heading text-sm font-semibold">Documento de transporte</div>
          </div>
          <TransportForm value={tr} onChange={setTr} bultos={bultos} onBultos={setBultos} />
          <div className="mt-4 pt-3 border-t border-border/60 space-y-2">
            <TemplatePicker tipo="guia_remision" selectClassName="w-full bg-app border border-border rounded px-2 py-1.5 text-xs" />
            <button onClick={() => save(false)} disabled={busy} data-testid="guide-save"
              className="w-full flex items-center justify-center gap-2 border border-agro-green/50 text-agro-green rounded-md py-2 text-sm font-medium hover:bg-agro-green/10 disabled:opacity-50">
              <FloppyDiskIcon size={16} /> Guardar y actualizar vista previa
            </button>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={download} disabled={busy} data-testid="guide-server-pdf"
                className="flex items-center justify-center gap-1.5 border border-border rounded-md py-2 text-xs hover:border-agro-green/50">
                <DownloadSimpleIcon size={14} /> PDF
              </button>
              <button onClick={async () => { if (await save(true)) setEmailOpen(true); }} disabled={busy} data-testid="guide-send-email"
                className="flex items-center justify-center gap-1.5 border border-border rounded-md py-2 text-xs hover:border-agro-green/50">
                <EnvelopeIcon size={14} /> Email
              </button>
              <button onClick={print} disabled={busy} data-testid="print-btn"
                className="flex items-center justify-center gap-1.5 bg-agro-green text-black font-semibold rounded-md py-2 text-xs hover:bg-agro-green-hover">
                <PrinterIcon size={14} weight="bold" /> Imprimir
              </button>
            </div>
            {!data.numero_guia && (
              <p className="text-[11px] text-text-muted">
                La vista previa es un borrador. El número de guía se asigna al descargar, imprimir o enviar.
              </p>
            )}
          </div>
          <EmailHistory items={data.email_history} title="Historial de envíos de la guía" />
        </div>
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden min-h-[520px]">
          {previewUrl ? (
            <iframe ref={frameRef} title="Guía de Remisión" src={previewUrl} data-testid="guide-preview"
              className="w-full h-[78vh] bg-white" />
          ) : (
            <div className="p-8 text-center text-text-muted">Generando vista previa…</div>
          )}
        </div>
      </div>

      {emailOpen && (
        <SendDocEmailModal title="Enviar Guía de Remisión por email" docLabel="Guía de Remisión"
          endpoint={`/shipping-guides/${id}/send-email`} clienteId={data.cliente_id}
          defaultTo={data.cliente_email || ""}
          defaultSubject={`Guía de Remisión ${data.numero_guia || data.factura_numero} · AGROPORT`}
          extraBody={{ destino_id: destinoId || undefined, bultos: bultos !== "" ? parseInt(bultos, 10) || 0 : undefined }}
          onClose={() => { setEmailOpen(false); load(); }} onSent={load} />
      )}
    </Layout>
  );
}
